/**
 * L1 — shared SOURCE FLOOR policy.
 *
 * The single place that decides how a RED or AMBER source report caps a site's
 * decision band.
 *
 * WHY THIS MODULE EXISTS. The cap used to be computed only inside
 * `buildExecutiveDecisionRollup`, which runs on SITE-level surfaces (the
 * Decision Guide, Path-to-GO). Every PORTFOLIO surface — snapshot, map, risk
 * matrix, decision queue, comparison view — reads the persisted `SiteComposite`
 * instead, and that carried the UNCAPPED band. A site whose Decision Guide
 * correctly read NO-GO therefore still rendered a green "GO" pill in the
 * portfolio and sorted to the top of the decision queue. Computing the floor
 * here and applying it inside `computeSiteComposite` makes the persisted band
 * already-capped, so every surface inherits it without any plumbing.
 *
 * Direction is MONOTONIC-DOWN: a floor may only ever lower a band, never raise
 * one. `computeSourceFloor` returns the strongest ceiling across the considered
 * reports, or `null` when nothing caps.
 *
 * NOTE ON VOCABULARY: this module speaks `DecisionBandId`
 * (`'go' | 'conditional' | 'conditionalHigh' | 'noGo'`), the band registry's
 * spelling. `executiveDecisionRollup` uses a second spelling for the same
 * concept (`'conditional-high'`, `'no-go'`); `sourceFloorToDerivedBand` maps
 * between them so the two engines cannot drift apart.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import { sourceFloorPolicy } from '../pathToGo/mandatoryReports';
import { getDisplayVerdict } from '../reportVerdict';
import { DECISION_BAND_RANK, type DecisionBandId } from './decisionBands';
import { isAdvisoryReportType } from './pillarRegistry';

// Re-exported for the existing importers. The definition moved to
// `decisionBands` so `deriveSiteVerdict` can compare bands without importing
// this module (which would drag `mandatoryReports` + `reportVerdict` with it).
export { DECISION_BAND_RANK };

export interface SourceFloor {
  /** The strongest ceiling imposed by a source report. */
  ceiling: DecisionBandId;
  /** The report that imposed it. */
  reportType: ReportTypeId;
  /** That report's own display verdict label, for the rationale. */
  label: string;
  /**
   * True when the constraint is reversible (`MITIGABLE_FLOOR_SOURCES`). A
   * mitigable red caps at CONDITIONAL and can never assert NO-GO on its own;
   * naming it lets the rationale distinguish an addressable constraint from a
   * hard one, which otherwise read identically.
   */
  mitigable: boolean;
}

/**
 * Translate a report's display tier into the best band it still permits.
 *
 * 'hard'      → red imposes NO-GO; amber imposes CONDITIONAL.
 * 'mitigable' → CONDITIONAL for both.
 *
 * 'green' / 'none' never cap and are filtered before this is called.
 */
function tierToCeiling(tier: string, mitigable: boolean): DecisionBandId {
  if (mitigable) return 'conditional';
  return tier === 'red' ? 'noGo' : 'conditional';
}

/** Latest report of `type` (newest `createdAt` wins). */
function latestOfType(
  reports: readonly SiteRagReport[],
  type: ReportTypeId,
): SiteRagReport | null {
  const matches = reports.filter((r) => (r.reportType ?? 'feasibility') === type);
  if (matches.length === 0) return null;
  return [...matches].sort(
    (a, b) => Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? ''),
  )[0] ?? null;
}

/**
 * The strongest ceiling imposed by any of `types`, or null when none caps.
 *
 * `types` is supplied by the caller rather than derived here, because the two
 * engines currently consider different sets and must be widened together: the
 * rollup builds rows over the MANDATORY set only, which is why a red
 * environmental/water/sentiment report on a site whose intent does not make it
 * mandatory contributes no floor at all. Passing the set in keeps that decision
 * in one place per caller instead of hiding it inside this function.
 */
export function computeSourceFloor(
  reports: readonly SiteRagReport[],
  types: readonly ReportTypeId[],
  options: {
    /**
     * Types whose red may assert NO-GO. A considered type OUTSIDE this set is
     * treated as mitigable: it still blocks an unconditional GO, but cannot
     * decline the site on its own.
     *
     * Omit to treat every considered type as mandatory (the original
     * behaviour, retained for callers that pass only the required set).
     */
    mandatory?: readonly ReportTypeId[];
    intent?: ProjectIntent | null;
  } = {},
): SourceFloor | null {
  const { mandatory, intent } = options;
  let strongest: SourceFloor | null = null;
  for (const type of types) {
    // An ADVISORY analysis may never move the band. Connectivity is scored and
    // displayed but is excluded from the composite aggregate, the unmeasured-
    // weight charge and the adequacy ratio, and `CONNECTIVITY_GATES_GO` is
    // false, so `requiredReportsForGo` can never contain it — which meant the
    // `nonMandatory` branch below read it as merely mitigable and let an amber
    // Connectivity report cap a site at CONDITIONAL. The rollup meanwhile
    // files the same report under `advisoryVerdicts`, which no PATH iterates,
    // and prints "connectivity is informational and does not gate the GO/NO-GO
    // call". Two engines publishing different calls from one report is worse
    // than either call alone.
    //
    // The advisory flag is the SAME predicate the composite uses, so the two
    // cannot drift: a pillar excluded from the aggregate is excluded here too.
    if (isAdvisoryReportType(type)) continue;
    const report = latestOfType(reports, type);
    if (!report) continue;
    const verdict = getDisplayVerdict(report, intent ?? undefined);

    /**
     * A report whose evidence is PROVABLY absent still caps.
     *
     * `qaFindings.sectionOmissions` is stamped at generation time and means
     * something stronger than "unscored": the report's evidence domain was
     * searched and demonstrably has nothing, so its sections were omitted
     * rather than written. Such a report carries no headline score, so
     * `getDisplayVerdict` returns tier 'none' and the loop would skip it.
     *
     * Skipping is the wrong direction. It would mean a site LOSES a ceiling by
     * having its evidence gap correctly identified — better than the same site
     * with a fabricated score, which is precisely backwards. Absence of
     * evidence is not a clean result; it is an open question, and an open
     * question caps at CONDITIONAL exactly as a mitigable constraint does.
     *
     * Read straight off the report rather than through `omittedSectionIds` so
     * this module does not pick up `sectionDepthGate` as a dependency.
     */
    const evidenceProvablyAbsent =
      (report.qaFindings?.sectionOmissions?.length ?? 0) > 0;
    const tier = verdict.tier === 'none' && evidenceProvablyAbsent
      ? 'amber'
      : verdict.tier;
    const label = verdict.tier === 'none' && evidenceProvablyAbsent
      ? 'no usable evidence'
      : verdict.label;
    if (tier === 'green' || tier === 'none') continue;
    // A report the intent never made mandatory still carries real evidence —
    // it simply has not been designated part of the decision gate, so it caps
    // rather than declines. Previously such a report had NO floor authority at
    // all: a red environmental, water or sentiment analysis on a site whose
    // intent did not trigger its gate could not move the call by one band.
    const nonMandatory = mandatory != null && !mandatory.includes(type);
    const mitigable = nonMandatory || sourceFloorPolicy(type) === 'mitigable';
    const ceiling = tierToCeiling(tier, mitigable);
    if (
      strongest === null
      || DECISION_BAND_RANK[ceiling] < DECISION_BAND_RANK[strongest.ceiling]
    ) {
      strongest = { ceiling, reportType: type, label, mitigable };
    }
  }
  return strongest;
}

/** True when `floor` would actually lower `band` (monotonic-down guard). */
export function floorLowers(
  floor: SourceFloor | null,
  band: DecisionBandId,
): floor is SourceFloor {
  return (
    floor !== null
    && DECISION_BAND_RANK[floor.ceiling] < DECISION_BAND_RANK[band]
  );
}

/** One sentence naming the floor, for the composite rationale. */
export function describeSourceFloor(floor: SourceFloor): string {
  return floor.mitigable
    ? `The ${floor.reportType} analysis reads ${floor.label}; a mitigable constraint caps the call at CONDITIONAL.`
    : `The ${floor.reportType} analysis reads ${floor.label}; the overall call cannot exceed it.`;
}
